// src/components/premium/PremiumGate.jsx
//
// The lock, and what it says when it closes.
//
// Fleet template alongside accessModel.js — a sibling app copies this file and
// changes the copy, not the structure.
//
// A gate that simply says "no" converts nobody. Each of these names the thing
// being withheld and what it costs to open it, and every gate offers a way back
// to something free so a locked door is never a dead end.
//
// Australian English throughout.

import React from 'react';
import { Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Lock, Sparkles } from 'lucide-react';
import { usePremium } from '../../context/PremiumContext';
import { modulesByLevel } from '../../data/modules';
import { MODULE_TOOLS } from '../../data/moduleTools';
import { courseValue, formatHours } from '../../data/accessModel';

const VALUE = courseValue(Object.values(modulesByLevel).flat(), MODULE_TOOLS);
import Card from '../shared/Card';
import Button from '../shared/Button';

const HUB_UPGRADE_URL = 'https://auth.divedeepdialogues.com/account/upgrade';

/**
 * The upsell card itself.
 *
 * `kind` picks which half of the ladder is being sold:
 *   'forever'      — the one-off purchase (content)
 *   'subscription' — a DDD plan (the companion beyond her free allowance).
 *                    Never sold as part of the one-off. Cloud sync is not
 *                    sold at all: any signed-in account has it.
 */
export const UpgradePrompt = ({
  title,
  description,
  kind = 'forever',
  freeAlternative = null,
  paywallDetail = null,
}) => {
  const subscription = kind === 'subscription';

  return (
    <Card className="max-w-xl mx-auto text-center p-6 sm:p-8">
      <div
        className="w-12 h-12 rounded-full bg-bright-gold/15 flex items-center justify-center mx-auto mb-4"
        aria-hidden="true"
      >
        {subscription ? (
          <Sparkles className="w-6 h-6 text-bright-gold" />
        ) : (
          <Lock className="w-6 h-6 text-bright-gold" />
        )}
      </div>

      <h2 className="text-xl sm:text-2xl font-bold text-pearl mb-2">{title}</h2>
      <p className="text-sm sm:text-base text-pearl/75 mb-6">{description}</p>

      {subscription ? (
        Capacitor.isNativePlatform() ? (
          // App Store rule 3.1.1: a native build may not link to, price, or otherwise
          // steer towards a purchase made outside the store. Existing members simply
          // sign in, which is the shape Pemberton was approved with.
          <p className="text-sm text-pearl/70">
            Already a Dive Deep Dialogues member? Sign in with the same account and your
            membership applies here.
          </p>
        ) : (
          <a href={HUB_UPGRADE_URL} className="inline-block">
            <Button size="medium">See DDD plans</Button>
          </a>
        )
      ) : (
        // SEAM FOR THE PURCHASE WORK: this opens the paywall once RevenueCat
        // lands. Until then the whole component is unreachable: beta mode
        // leaves everything unlocked.
        <Button size="medium" onClick={() => window.dispatchEvent(new CustomEvent('pph:open-paywall', { detail: paywallDetail }))}>
          Unlock Profound Philosophy
        </Button>
      )}

      {freeAlternative && (
        <p className="mt-5 text-sm text-pearl/60">
          {freeAlternative}
        </p>
      )}
    </Card>
  );
};

/**
 * Gate a module by its `order` (see src/data/modules.js).
 */
export const ModuleGate = ({ order, title, children }) => {
  const { canAccessModule } = usePremium();
  if (canAccessModule(order)) return children;

  return (
    <UpgradePrompt
      title={title ? `Continue with ${title}` : 'Continue with the full course'}
      description={`It’s part of the full course: ${VALUE.paidModuleCount} more modules, about ${formatHours(VALUE.paidMinutes)} of the course, ${VALUE.paidToolCount} more tools, and the Original Position’s group modes. One payment, yours for good.`}
      paywallDetail={title ? { nextModuleTitle: title } : null}
      freeAlternative={
        <>
          The first two modules stay free. Start with{' '}
          <Link to="/modules/what-is-philosophy" className="text-bright-gold underline">
            What Is Philosophy?
          </Link>
        </>
      }
    />
  );
};

/**
 * Gate a Toolkit tool by id.
 */
export const ToolGate = ({ toolId, title, children }) => {
  const { canAccessTool } = usePremium();
  if (canAccessTool(toolId)) return children;

  return (
    <UpgradePrompt
      title={title ? `${title} is a full-course tool` : 'This tool is part of the full course'}
      description="Eighteen tools that turn the reading into practice, plus all eight modules. One payment, yours for good."
      freeAlternative={
        <>
          Four tools, and the Original Position played solo, are free:{' '}
          <Link to="/toolkit" className="text-bright-gold underline">
            see the Toolkit
          </Link>
        </>
      }
    />
  );
};

/**
 * Gate one mode of the Original Position.
 *
 * Solo and joining someone else's linked table are always free: the host has
 * paid, and charging their guests would close the one channel by which this
 * app spreads.
 */
export const GameModeGate = ({ mode, onBack, children }) => {
  const { canAccessGameMode } = usePremium();
  if (canAccessGameMode(mode)) return children;

  const parlour = mode === 'parlour';

  return (
    <div className="py-8 px-4">
      {onBack && (
        <div className="max-w-xl mx-auto mb-4">
          <Button size="small" variant="ghost" onClick={onBack}>← Back to the three ways in</Button>
        </div>
      )}
      <UpgradePrompt
        title={parlour ? 'Parlour is part of the full course' : 'Hosting a linked table is part of the full course'}
        description={
          parlour
            ? 'Pass one phone around a table, deal everyone a private stake in the society they are about to design, and argue it out. Included with the full course.'
            : 'Host a table across everyone’s own phones, with private hints and a shared fate. Included with the full course, and anyone you invite plays free.'
        }
        freeAlternative="Solo play is free, and always will be: the whole game, on your own."
      />
    </div>
  );
};

export default UpgradePrompt;
